"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight,
  BookOpen,
  BrainCircuit,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  Loader2,
  MessageSquare,
  NotebookPen,
  RefreshCw,
  Search,
  UserRound,
  Wand2,
  type LucideIcon,
} from "lucide-react";

import {
  ARCHIVE_TYPES,
  fetchArchivePage,
  isArchivePageNotFound,
  type ArchiveItem,
  type ArchivePage,
  type ArchiveType,
} from "@/lib/archive-api";
import { formatRelativeTime } from "@/lib/relative-time";
import { useAppShell } from "@/context/AppShellContext";
import { listKnowledgeBases } from "@/lib/knowledge-api";
import { selectKnowledgeScope } from "@/lib/knowledge-scope";
import { KnowledgeScopeSelect } from "@/components/knowledge/KnowledgeScopeSelect";

type KnowledgeScopeLoadState = "loading" | "ready" | "failed";
interface ArchiveTypeConfig {
  type: ArchiveType;
  /** English source string; rendered through i18next `t()`. */
  label: string;
  detailHref: string;
  icon: LucideIcon;
}

const archiveTypeConfigs: ArchiveTypeConfig[] = [
  {
    type: "chat_history",
    label: "Chat History",
    detailHref: "/space/chat-history",
    icon: MessageSquare,
  },
  {
    type: "learning_record",
    label: "Learning records",
    detailHref: "/space/learning",
    icon: GraduationCap,
  },
  {
    type: "note",
    label: "Notes",
    detailHref: "/space/notebooks",
    icon: NotebookPen,
  },
  {
    type: "persona",
    label: "Personas",
    detailHref: "/space/personas",
    icon: UserRound,
  },
  {
    type: "question_bank",
    label: "Question Bank",
    detailHref: "/space/questions",
    icon: ClipboardList,
  },
  {
    type: "skill",
    label: "Skills",
    detailHref: "/space/skills",
    icon: Wand2,
  },
];

const typeConfigByType = Object.fromEntries(
  archiveTypeConfigs.map((config) => [config.type, config]),
) as Record<ArchiveType, ArchiveTypeConfig>;

function itemHref(item: ArchiveItem): string {
  const targetKind = item.target.kind;
  if (targetKind === "session" && typeof item.target.session_id === "string") {
    return `/home/${encodeURIComponent(item.target.session_id)}`;
  }
  if (
    targetKind === "co_writer_document" &&
    typeof item.target.document_id === "string"
  ) {
    return `/talk-note/${encodeURIComponent(item.target.document_id)}`;
  }
  return typeConfigByType[item.type].detailHref;
}

function itemMeta(meta: Record<string, unknown>): string {
  return Object.values(meta)
    .flatMap((value) => {
      if (typeof value === "string" || typeof value === "number") {
        const display = String(value).trim();
        return display ? [display.slice(0, 100)] : [];
      }
      return [];
    })
    .slice(0, 2)
    .join(" · ");
}

function itemDescription(item: ArchiveItem, t: (key: string) => string): string {
  const metadata = itemMeta(item.meta);
  if (item.snippet && metadata) return `${item.snippet} · ${metadata}`;
  return item.snippet || metadata || t("No extra detail");
}

function ArchiveCountSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="h-[38px] w-28 animate-pulse rounded-full bg-[var(--muted)]"
    />
  );
}

function ArchiveRowsSkeleton() {
  const { t } = useTranslation();
  return (
    <div aria-label={t("Loading the archive")} className="divide-y divide-[var(--border)]">
      {Array.from({ length: 5 }, (_, index) => (
        <div
          key={index}
          aria-hidden="true"
          className="flex h-[68px] animate-pulse items-center gap-3 px-4"
        >
          <div className="h-8 w-8 rounded-lg bg-[var(--muted)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-44 rounded bg-[var(--muted)]" />
            <div className="h-3 w-72 max-w-full rounded bg-[var(--muted)]" />
          </div>
          <div className="h-5 w-16 rounded-full bg-[var(--muted)]" />
        </div>
      ))}
    </div>
  );
}

export default function ArchiveView() {
  const { t } = useTranslation();
  const { activeKnowledge } = useAppShell();
  const [initialKnowledge] = useState(activeKnowledge);
  const [selectedKnowledge, setSelectedKnowledge] = useState<
    string | null | undefined
  >(undefined);
  const [knowledgeOptions, setKnowledgeOptions] = useState<string[]>([]);
  const [knowledgeScopeLoadState, setKnowledgeScopeLoadState] =
    useState<KnowledgeScopeLoadState>("loading");
  const [selectedType, setSelectedType] = useState<ArchiveType>("chat_history");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState<ArchivePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestSequence = useRef(0);
  const scopeKnowledge = selectKnowledgeScope(
    knowledgeOptions,
    selectedKnowledge === undefined ? initialKnowledge : selectedKnowledge,
  );

  useEffect(() => {
    let active = true;

    void listKnowledgeBases()
      .then((knowledgeBases) => {
        if (!active) return;
        setKnowledgeOptions(
          Array.from(
            new Set(
              knowledgeBases
                .map((knowledgeBase) => knowledgeBase.name.trim())
                .filter(Boolean),
            ),
          ),
        );
        setKnowledgeScopeLoadState("ready");
      })
      .catch(() => {
        if (!active) return;
        setKnowledgeOptions([]);
        setKnowledgeScopeLoadState("failed");
      });

    return () => {
      active = false;
    };
  }, []);

  const loadFirstPage = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setPage(null);
    setError(null);
    setNotice(null);

    try {
      const nextPage = await fetchArchivePage({
        type: selectedType,
        q: query,
        knowledge: scopeKnowledge ?? undefined,
      });
      if (requestId !== requestSequence.current) return;
      setPage(nextPage);
    } catch (caught) {
      if (requestId !== requestSequence.current) return;
      setError(
        caught instanceof Error ? caught.message : t("An unknown error occurred."),
      );
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [query, scopeKnowledge, selectedType]);

  useEffect(() => {
    if (knowledgeScopeLoadState === "loading") return;
    void loadFirstPage();
  }, [knowledgeScopeLoadState, loadFirstPage, reloadKey]);

  const loadNextPage = useCallback(async () => {
    const cursor = page?.page.nextCursor;
    if (!cursor || loadingMore) return;

    const requestId = ++requestSequence.current;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = await fetchArchivePage({
        type: selectedType,
        q: query,
        cursor,
        knowledge: scopeKnowledge ?? undefined,
      });
      if (requestId !== requestSequence.current) return;
      setPage((current) =>
        current
          ? { ...nextPage, items: [...current.items, ...nextPage.items] }
          : nextPage,
      );
    } catch (caught) {
      if (requestId !== requestSequence.current) return;
      if (!isArchivePageNotFound(caught)) {
        setError(
          caught instanceof Error ? caught.message : t("An unknown error occurred."),
        );
        return;
      }

      setPage(null);
      try {
        const restartedPage = await fetchArchivePage({
          type: selectedType,
          q: query,
          knowledge: scopeKnowledge ?? undefined,
        });
        if (requestId !== requestSequence.current) return;
        setPage(restartedPage);
        setNotice(t("The page changed, so we reloaded from the first page."));
      } catch (restartError) {
        if (requestId !== requestSequence.current) return;
        setError(
          restartError instanceof Error
            ? restartError.message
            : t("Couldn't reload the first page."),
        );
      }
    } finally {
      if (requestId === requestSequence.current) setLoadingMore(false);
    }
  }, [loadingMore, page?.page.nextCursor, query, scopeKnowledge, selectedType]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextQuery = searchInput.trim();
    if (nextQuery === query) {
      setReloadKey((current) => current + 1);
    } else {
      setQuery(nextQuery);
    }
  };
  const selectArchiveKnowledge = useCallback((knowledge: string | null) => {
    requestSequence.current += 1;
    setSelectedKnowledge(knowledge);
    setPage(null);
    setLoadingMore(false);
    setError(null);
    setNotice(null);
  }, []);

  const selectedConfig = typeConfigByType[selectedType];

  return (
    <div className="h-full overflow-y-auto [scrollbar-gutter:stable]">
      <main className="mx-auto max-w-5xl px-5 py-5 pb-12 sm:px-8">
        <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h1 className="text-[18px] font-extrabold tracking-tight text-[var(--foreground)]">
            {t("My archive")}
          </h1>
          <p className="text-[13.5px] text-[var(--muted-foreground)]">
            {t("Home curates; this keeps everything — for search and bulk clean-up")}
          </p>
        </header>

        {knowledgeScopeLoadState === "ready" && (
          <>
            <section className="mt-3">
              <KnowledgeScopeSelect
                options={knowledgeOptions}
                value={scopeKnowledge}
                onChange={selectArchiveKnowledge}
              />
            </section>
            {scopeKnowledge !== null && (
              <p className="mt-1 text-[13.5px] text-[var(--muted-foreground)]">
                {t("Knowledge filters apply to chats, learning records, and notes.")}
              </p>
            )}
          </>
        )}
        <section
          aria-label={t("Archive type")}
          className="mt-3 flex flex-wrap gap-1.5"
        >
          {loading && !page
            ? ARCHIVE_TYPES.map((type) => <ArchiveCountSkeleton key={type} />)
            : archiveTypeConfigs.map((config) => {
                const Icon = config.icon;
                const selected = config.type === selectedType;
                const count = page?.counts[config.type];
                return (
                  <div
                    key={config.type}
                    className={`inline-flex h-[38px] items-center rounded-full border transition-colors ${
                      selected
                        ? "border-[var(--primary)] bg-[var(--primary)] text-[var(--primary-foreground)]"
                        : "border-[var(--border)] bg-[var(--card)] text-[var(--muted-foreground)]"
                    }`}
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedType(config.type)}
                      className="inline-flex h-full items-center gap-1.5 rounded-l-full px-3 text-[13.5px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]"
                    >
                      <Icon size={14} strokeWidth={1.8} />
                      <span>{t(config.label)}</span>
                      <span className="tabular-nums">{count ?? "—"}</span>
                    </button>
                    <Link
                      href={config.detailHref}
                      aria-label={t("View {{label}} details", { label: t(config.label) })}
                      className={`mr-1 inline-flex rounded-full p-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--ring)] ${
                        selected
                          ? "hover:bg-white/15"
                          : "hover:bg-[var(--muted)]"
                      }`}
                    >
                      <ArrowUpRight size={13} aria-hidden="true" />
                    </Link>
                  </div>
                );
              })}
        </section>

        <form
          aria-label={t("Search the archive")}
          onSubmit={submitSearch}
          className="mt-4 flex h-11 items-center rounded-xl border border-[var(--border)] bg-[var(--card)] px-3 focus-within:border-[var(--ring)]"
        >
          <Search
            size={17}
            strokeWidth={1.8}
            className="shrink-0 text-[var(--muted-foreground)]"
            aria-hidden="true"
          />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t("Search chat titles and bodies")}
            className="min-w-0 flex-1 bg-transparent px-2 text-[13.5px] text-[var(--foreground)] outline-none placeholder:text-[var(--muted-foreground)]"
          />
          <button
            type="submit"
            aria-label={t("Search")}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <Search size={15} aria-hidden="true" />
          </button>
        </form>

        {notice && (
          <p role="status" className="mt-3 text-[13.5px] text-[var(--muted-foreground)]">
            {notice}
          </p>
        )}

        {page?.partial && (
          <div
            role="alert"
            className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-[13.5px] text-[var(--foreground)]"
          >
            <span>
              {t(
                "Couldn't verify persona sharing, so personas are excluded from these partial results.",
              )}
            </span>
            <button
              type="button"
              onClick={() => setReloadKey((current) => current + 1)}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium text-[var(--foreground)] hover:bg-[var(--card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              <RefreshCw size={13} aria-hidden="true" />
              {t("Retry")}
            </button>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-[13.5px] text-[var(--foreground)]"
          >
            <span>{t("Couldn't load the archive.")} {error}</span>
            <button
              type="button"
              onClick={() => setReloadKey((current) => current + 1)}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium hover:bg-[var(--card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              <RefreshCw size={13} aria-hidden="true" />
              {t("Retry")}
            </button>
          </div>
        )}

        {page ? (
          <section
            aria-label={
              query
                ? t("Search results for {{query}}", { query })
                : t("{{label}} archive", { label: t(selectedConfig.label) })
            }
            aria-busy={loading}
            className="mt-3 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)]"
          >
            {page.items.length > 0 ? (
              <div className="divide-y divide-[var(--border)]">
                {page.items.map((item) => {
                  const Icon = typeConfigByType[item.type].icon;
                  return (
                    <Link
                      key={item.id}
                      href={itemHref(item)}
                      className="flex min-h-[68px] items-center gap-3 px-4 py-3 transition-colors hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ring)]"
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--muted)] text-[var(--primary)]">
                        <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold text-[var(--foreground)]">
                          {item.title}
                        </span>
                        <span className="mt-0.5 block truncate text-[13.5px] text-[var(--muted-foreground)]">
                          {itemDescription(item, t)}
                        </span>
                      </span>
                      <span className="rounded-full bg-[var(--secondary)] px-2 py-0.5 text-[13px] font-semibold text-[var(--primary)]">
                        {t(item.anchor.badge)}
                      </span>
                      <span className="hidden shrink-0 text-[13.5px] text-[var(--muted-foreground)] sm:block">
                        {formatRelativeTime(Date.parse(item.when) / 1000, "ko")}
                      </span>
                      <ChevronRight
                        size={15}
                        className="shrink-0 text-[var(--muted-foreground)] sm:hidden"
                        aria-hidden="true"
                      />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-[13.5px] text-[var(--muted-foreground)]">
                {query
                  ? t("No archived items match your search.")
                  : t("Nothing archived under {{label}}.", {
                    label: t(selectedConfig.label),
                  })}
              </div>
            )}
          </section>
        ) : loading ? (
          <section
            aria-label={
              query
                ? t("Search results for {{query}}", { query })
                : t("{{label}} archive", { label: t(selectedConfig.label) })
            }
            aria-busy="true"
            className="mt-3 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)]"
          >
            <ArchiveRowsSkeleton />
          </section>
        ) : null}

        {page?.page.hasMore && page.page.nextCursor && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={() => void loadNextPage()}
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-[13.5px] font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--muted)] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              {loadingMore && <Loader2 size={14} className="animate-spin" />}
              {t("Show more")}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
